import { BaseService } from "../BaseService";
import { File, Folder, FolderContents } from "@/types/breeze/File";
import { PaginatedResult } from "@/types/breeze/Any";

export class FileService extends BaseService {
  // ── Files ──────────────────────────────────────────────────────────────

  public async getAll(
    page: number = 1,
    pageSize: number = 25,
    sortBy?: string,
    sortDirection: "asc" | "desc" = "asc",
    search?: string,
    fileType?: "image" | "video" | "audio" | "document",
    folderId?: number | null,
  ): Promise<PaginatedResult<File>> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      ...(sortBy && { sortBy }),
      sortDirection,
      ...(search && { search }),
      ...(fileType && { fileType }),
    });
    if (folderId !== undefined && folderId !== null) {
      params.append("folderId", folderId.toString());
    }
    return this.getRequest(`/files?${params.toString()}`);
  }

  public async getById(id: number): Promise<File> {
    return this.getRequest(`/files/${id}`);
  }

  public async create(
    formData: FormData,
    isPublic?: boolean,
    entityId?: number,
    fieldName?: string,
    folderId?: number,
    onProgress?: (percent: number) => void,
  ): Promise<File> {
    const params = new URLSearchParams();
    params.append("isPublic", String(isPublic ?? false));

    if (entityId !== undefined && entityId !== null) {
      params.append("entityId", String(entityId));
    }
    if (fieldName !== undefined && fieldName !== null && fieldName !== "") {
      params.append("fieldName", fieldName);
    }
    if (folderId !== undefined && folderId !== null) {
      params.append("folderId", String(folderId));
    }

    const path = `/files?${params.toString()}`;
    if (!onProgress) {
      return this.postFormDataRequest(path, formData, `File uploaded`);
    }

    const url = this.processUrl(path, true);
    const response = await this.client.post<File>(url, formData, {
      onUploadProgress: (event) => {
        if (event.total) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      },
    });
    return response.data;
  }

  public async reprobe(id: number): Promise<File> {
    return this.postRequest<undefined, File>(`/files/${id}/reprobe`, undefined, undefined, true, true);
  }

  public async updateFile(id: number, data: {
    original_file_name?: string;
    description?: string;
    alt_text?: string;
    tags?: string;
    custom_metadata?: string;
    folder_id?: number | null;
  }): Promise<File> {
    return this.putRequest(`/files/${id}`, data, "File updated");
  }

  public async deleteFile(id: string): Promise<void> {
    return this.deleteRequest(`/files/${id}`, `File deleted`);
  }

  public async getFilesByBucket(bucket: string): Promise<File[]> {
    return this.getRequest(`/files/bucket/${bucket}`);
  }

  /**
   * Fetches a short-lived presigned URL for the file. Drop it into an <img>/<audio>/<video>
   * `src` — element loads aren't subject to the CORS check that blocked the previous
   * axios-blob approach against the redirect endpoint.
   */
  public async getFileSignedUrl(id: number): Promise<string> {
    const result = await this.getRequest<{ url: string; expires_at: string }>(
      `/files/${id}/url`,
    );
    return result.url;
  }

  // ── Folders ────────────────────────────────────────────────────────────

  public async getFolders(): Promise<Folder[]> {
    return this.getRequest("/files/folders");
  }

  public async getFolderTree(): Promise<Folder[]> {
    return this.getRequest("/files/folders/tree");
  }

  public async getFolderContents(folderId: number): Promise<FolderContents> {
    return this.getRequest(`/files/folders/${folderId}`);
  }

  public async createFolder(
    data: {
      name: string;
      parent_folder_id?: number | null;
      is_personal?: boolean;
    },
    suppressSuccessMessage: boolean = false,
  ): Promise<Folder> {
    return this.postRequest("/files/folders", data, "Folder created", true, suppressSuccessMessage);
  }

  public async updateFolder(folderId: number, data: {
    name?: string;
    parent_folder_id?: number | null;
  }): Promise<Folder> {
    return this.putRequest(`/files/folders/${folderId}`, data, "Folder updated");
  }

  public async deleteFolder(folderId: number, deleteContents: boolean = false): Promise<void> {
    return this.deleteRequest(`/files/folders/${folderId}?deleteContents=${deleteContents}`, "Folder deleted");
  }
}

export const fileService = new FileService();
